import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router";
import PropTypes from 'prop-types';
import { IconButton, makeStyles, Tooltip } from '@material-ui/core';
import { AlertMessages, FormSelect, FormTextField } from '../../../components';
import ListContainer from "../../list";
import { ROUTES } from "../../../constants/routes";
import { getI18n } from '../../../utils/intl';
import cautionIcon from "../Icons/caution.svg";
import { fetchApiGroupsList as fetchApiGroupsListAction } from "../../../actions/apiGroups";
import {
  getApiGroupsList,
  getApiGroupsLoading, getApiGroupsError,
} from '../../../reducers/apiGroups';
import { getFeatureFlagApiPlans, getIsApiOwner, getIsPortalAdmin } from '../../../reducers/portalConfig';
import styles from './styles';
import { API_STATUS_DEPRECATED, API_STATUS_ENABLED } from '../../../constants';
import clsx from 'clsx';

const labels = {
  header: "label.api.groups",
  addButtonTitle: "label.apigroups.list.add.button.title",
  groupNameHeader: "label.application.details.apigroups.filter.name.placeholder",
  apiDeprecated: "label.apis.states.deprecated",
  apiEnabled: "label.apis.states.enabled",
  apiAll: "label.apis.filter.states.all",
  status: "label.status",
  noResults: "label.apigroups.no.results",
  applicationsCount: "label.applications",
  description: "label.description",
  filter: "label.filter",
  all: "label.all",
};

const Status = ({ value }) => {
  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  let className = null;
  const warningClass = clsx(classes.status, classes.warning);
  const successClass = clsx(classes.status, classes.success);

  let text = null;
  switch (value) {
    case API_STATUS_DEPRECATED: {
      className = warningClass;
      text = intl.getI18nMessage(labels.apiDeprecated);
      break;
    }
    case API_STATUS_ENABLED: {
      className = successClass;
      text = intl.getI18nMessage(labels.apiEnabled);
      break;
    }
  }

  if (!text) return null;

  return <span className={className}>
    {text}
  </span>
}

Status.propTypes = {
  value: PropTypes.string,
}

const getApiGroupsListColumns = (intl) => [
  {
    id: 'name',
    label: intl.getI18nMessage(labels.groupNameHeader),
    minWidth: 200,
    link: ROUTES.apiGroupsEdit.path,
    isCellOverflow: true,
  },
  {
    id: 'description',
    label: intl.getI18nMessage(labels.description),
    minWidth: 300,
    isCellOverflow: true,
  },
  {
    id: 'status',
    label: intl.getI18nMessage(labels.status),
    minWidth: 200,
    value: (item) => <Status value={item.status} />,
  },
];

const ApiGroupsList = () => {
  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const history = useHistory();
  const globalDispatch = useDispatch();
  const fetchApiGroupsList = (payload) => globalDispatch(fetchApiGroupsListAction(payload));

  const isApiOwner = useSelector(getIsApiOwner);
  const isPortalAdmin = useSelector(getIsPortalAdmin);
  const apiGroups = useSelector(getApiGroupsList);
  const isLoading = useSelector(getApiGroupsLoading);
  const error = useSelector(getApiGroupsError);
  const isApiPlansEnabled = useSelector(getFeatureFlagApiPlans);
  const [filterByStatus, setFilterByStatus] = useState('ALL');
  const [filterByName, setFilterByName] = useState('');
  const [filterName, setFilterName] = useState('');
  const [listRows, setListRows] = useState([]);
  const [alertMessages, setAlertMessages] = useState({
    message: '',
    variant: '',
    status: false,
  })

  useEffect(() => {
    fetchApiGroupsList();
  }, []);

  useEffect(() => {
    if (isApiPlansEnabled) {
      history.push(ROUTES.notFound.path);
    }
  }, [isApiPlansEnabled]);

  const onAddApiGroup = () => {
    history.push(ROUTES.apiGroupsAdd.path);
  };

  const noResultsMessage = intl.getI18nMessage(labels.noResults);
  const apiGroupsFetchErrors = error ? [{ error }] : [];

  const filterByStatusOptions = [
    { uuid: 'ALL', name: `${intl.getI18nMessage(labels.all)}` },
    { uuid: 'ENABLED', name: `${intl.getI18nMessage(labels.apiEnabled)}` },
    { uuid: 'DEPRECATED', name: `${intl.getI18nMessage(labels.apiDeprecated)}` },
  ];

  const handleFilterByName = (value) => {
    setFilterByName(value)
    setFilterName(value)
  }

  const handleFilterByStatus = (value) => {
    setFilterByStatus(value);
    applyFilters({ dropDownFilters: value });
  }

  const handleBlur = () => {
    applyFilters({ nameFilter: filterByName });
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  }

  const applyFilters = ({ nameFilter, dropDownFilters }) => {
    const nameFilterText = nameFilter || filterName;
    const dropDownFiltersToBeApplied = dropDownFilters || filterByStatus;
    const filteredList = apiGroups?.filter(item => {

      const qualifiedForName = !nameFilterText ||
        (item?.name && item.name.toUpperCase().includes(nameFilterText.toUpperCase()));

      const qualifiedForStatus = !dropDownFiltersToBeApplied ||
        dropDownFiltersToBeApplied === 'ALL' ||
        (item?.status && item.status.includes(dropDownFiltersToBeApplied));

      return qualifiedForName && qualifiedForStatus;
    })

    setListRows(filteredList);
  }

  useEffect(() => {
    setListRows(apiGroups || []);
  }, [apiGroups])

  useEffect(() => {
    if (!isApiOwner && !isPortalAdmin) {
      history.push('/404');
    }
  }, [isApiOwner, isPortalAdmin])

  return (
    <>
      {
        alertMessages.status &&
        <AlertMessages
          id='add-api-group-client-snackbar'
          className={classes.alertMessagesContainer}
          message={alertMessages.message}
          variant={alertMessages.variant}
          onClose={() => setAlertMessages({ ...alertMessages, status: false })}
          shouldAutoCloseSuccessMessages
        />
      }
      <ListContainer
        listPageId='api-groups-page'
        hidePagination
        pageHeaderTooltipTitle={intl.getI18nMessage("label.api.groups.list.tool.tip.message")}
        pageHeaderTitle={intl.getI18nMessage(labels.header)}
        addButtonLabel={intl.getI18nMessage(labels.addButtonTitle)}
        rows={listRows}
        columns={getApiGroupsListColumns(intl)}
        onAdd={onAddApiGroup}
        noResultsMessage={noResultsMessage}
        isLoading={isLoading}
        errors={apiGroupsFetchErrors}
        headerEndAdornment={
          <>
            <Tooltip
              arrow disableFocusListener disableTouchListener placement="top"
              title={intl.getI18nMessage("label.api.groups.list.caution.tool.tip.message")}
            >
              <IconButton
                size="small" edge="start"
              >
                <img src={cautionIcon} className={classes.helpIcon} />
              </IconButton>
            </Tooltip>
          </>
        }
        filterAndSortContent={(
          <div className={classes.flex}>
            <FormTextField
              id='api-groups-filter-by-name'
              name={intl.getI18nMessage("label.filter")}
              placeholder={intl.getI18nMessage("label.application.details.apigroups.table.columns.apigroup.title")}
              handleChange={(value) => handleFilterByName(value)}
              value={filterByName}
              handleBlur={handleBlur}
              onKeyPress={handleKeyPress}
              fieldContainerClass={classes.nameFilter}
            />
            <FormSelect
              id='api-groups-filter-by-status'
              noNoneOption
              noNativeSelect
              value={filterByStatus}
              data={filterByStatusOptions}
              handleChange={(value) => handleFilterByStatus(value)}
              selectFieldClass={classes.statusFilter}
            />
          </div>
        )}
      />
    </>
  )
}

export default ApiGroupsList;